import {sampleColors} from "../../../utils/colors";
import {MongoQueryBuilder} from "../../../utils/mongoQuery";
import {fetchFilteredTickers} from "../../../api/tickersApi";
import {shortenSector} from "../../../utils/abbreviations";

export const mergeEtfs = (etfs) => {
    // Step 1: Calculate the total weight sum
    let totalWeight = etfs.reduce((sum, etf) => sum + etf.weight, 0);
    // If total weight is 0, assign equal weight to all
    if (totalWeight === 0) {
        const equalWeight = 1 / etfs.length;
        etfs.forEach(etf => {
            etf.normWeight = equalWeight
            etf.weight = equalWeight
        });
    }
    // If the total weight is less than 1, normalize to sum to 1
    etfs.forEach(etf => {
        etf.normWeight = etf.weight / totalWeight;
    });


    // Step 2: Create prorated stock holdings
    const combinedHoldings = {};

    etfs.forEach(etf => {
        const etfWeight = etf.weight;  // Normalized weight of the ETF
        const normWeight = etf.normWeight || etfWeight;  // Normalized weight of the ETF
        const etfSymbol = etf.symbol;  // ETF symbol

        etf.holdings.forEach(holding => {
            const stockSymbol = holding.symbol;  // Stock symbol in the holding
            const holdingWeight = holding.wgt;  // Weight of the stock in the ETF

            // Calculate prorated stock weight
            const proratedWeight = holdingWeight * normWeight;
            const originalWeight = holdingWeight * etfWeight;

            // If stock already exists in combinedHoldings, sum the prorated weights
            if (combinedHoldings[stockSymbol]) {
                combinedHoldings[stockSymbol].wgt += proratedWeight;
                combinedHoldings[stockSymbol].originalWeight += originalWeight;

                combinedHoldings[stockSymbol].parents.push(etfSymbol);  // Add ETF symbol to parents
            } else {
                // If the stock does not exist yet, add it to combinedHoldings
                combinedHoldings[stockSymbol] = {
                    ...holding,
                    symbol: stockSymbol,
                    wgt: proratedWeight,
                    originalWeight,
                    parents: [etfSymbol],  // Initialize with the current ETF symbol

                };
            }
        });
    });

    // Step 3: Convert combinedHoldings object to an array
    const mergedHoldings = Object.values(combinedHoldings);

    return mergedHoldings;
};


export const cutMergedHoldings = (mergedHoldings, cutoffWeight) => {
    // Step 1: Sort the holdings by weight in descending order
    const sortedHoldings = mergedHoldings.sort((a, b) => b.wgt - a.wgt);

    // Step 2: Calculate cumulative weight and cut at the cutoff point
    let cumulativeWeight = 0;
    let cutIndex = sortedHoldings.length; // Default to return all holdings if none exceed cutoffWeight

    for (let i = 0; i < sortedHoldings.length; i++) {
        cumulativeWeight += sortedHoldings[i].wgt;

        // If the cumulative weight exceeds the cutoff weight, cut here
        if (cumulativeWeight > cutoffWeight) {
            cutIndex = i;
            break;
        }
    }

    // Step 3: Return the array from the start to the cutoff point
    return sortedHoldings.slice(0, cutIndex + 1);  // +1 to include the cutoff point ETF
};


export const getSectorColors = (mergedHoldings)=>{
    const uniqueSectors = Array.from(new Set(mergedHoldings.map(holding => holding.sector)));
    const colorPalette = sampleColors(uniqueSectors.length, null, 'dark');
    return uniqueSectors.reduce((acc, current, index) => {
      acc[current] = colorPalette[index];
      return acc;
    }, {});
}



const _aggregateSectors = (kvList)=> {
    let mapping = {};
    kvList.forEach(pair => {
        let [wgt, field] = pair;
        if (!mapping.hasOwnProperty(field)) {
            mapping[field] = {
                count: 0,
                wgt: 0
            };
        }
        mapping[field].wgt += wgt;
        mapping[field].count += 1;
    });
    return mapping;
}


const _makeBarRows = (set) => {
    return set.map((x)=>{
        return [x.wgt, x?.sector ?? 'missing'];
    })
}


/**
 * Prepare data for the grouped bar chart
 * @param cutHoldings
 * @param fullHoldings
 * @param displayField
 * @returns {*[]}
 */
export const getSectorsBarData = (cutHoldings, fullHoldings, displayField) => {
    let subsetMap = _aggregateSectors(_makeBarRows(cutHoldings), displayField);
    let fullsetMap = _aggregateSectors(_makeBarRows(fullHoldings), displayField);

    let data = [];
    for (let sector in fullsetMap) {
        let mult = displayField === 'wgt' ? 100 : 1; // Multiply by 100 if we're dealing with weights
        if (fullsetMap[sector]?.[displayField] > 0.01) {
            data.push({
                id: shortenSector(sector),
                'Full Portfolio': fullsetMap[sector][displayField] * mult,
                'Reduced Stocks': subsetMap[sector]?.[displayField] * mult ?? 0
            })
        }
    }
    data.sort((a, b) => a['Full Portfolio'] - b['Full Portfolio']);
    return data;
};

export const computeSectorDeviations = (sectorDistribution, originalSectorDistribution) => {
    const result = [];

    // Track sectors we've already processed
    const processedSectors = new Set();

    // Iterate over the original sector distribution
    for (const sector in originalSectorDistribution) {
        const originalWeight = originalSectorDistribution[sector];
        const subsetWeight = sectorDistribution[sector] || 0;  // If missing, use 0

        // Calculate the delta
        const delta = subsetWeight > 0 ? subsetWeight - originalWeight : -originalWeight;

        // Check if the sector exists in both distributions
        const inBoth = subsetWeight > 0;

        result.push({
            name: sector,
            delta,
            inBoth,
            originalWeight,
            newWeight: subsetWeight
        });

        // Mark this sector as processed
        processedSectors.add(sector);
    }

    // Check for sectors that are in the subset but not in the original
    for (const sector in sectorDistribution) {
        if (!processedSectors.has(sector)) {
            result.push({
                name: sector,
                delta: -sectorDistribution[sector],  // Negative delta since it wasn't in the original
                inBoth: false
            });
        }
    }
    return result;
};


/**
 *
 * @param tickerList {Array[object]} containing 'symbol' and 'sector' fields
 * @param blacklist {Object[str, Array[str]]} containing fields 'sectors' and 'symbols' with any sectors or symbols to exclude
 */
export const applyBlacklist = (tickerList, blacklist) => {
    if (!blacklist) return {
        tickerList, removed: 0
    };
    const filteredTickers = tickerList.filter(item =>
        !blacklist.tickers.includes(item.symbol) &&
        !blacklist.sectors.includes(item.sector)
    );
    return {
        filteredTickers,
        removed: tickerList.length - filteredTickers.length
    };
};



export const applyScreen = async (dispatch, screen, tickerList)=>{
    const symbols = tickerList.map(ticker => ticker.symbol);
    // Build Mongo Query
    const mongoQuery = MongoQueryBuilder.fromMongoQuery(screen.query);
    mongoQuery.inList('symbol', symbols);
    mongoQuery.projectionFields._id = 0;
    const query = mongoQuery.toMongoQuery();

    // Execute query
    const filteredResponse = await dispatch(fetchFilteredTickers(query)).unwrap();
    const screenedTickers = filteredResponse.map(ticker => ({
            ...tickerList.find(s => s.symbol === ticker.symbol),
            ...ticker,
    })).filter(Boolean); // Filter out undefined results
    return {
        screenedTickers,
        removed: tickerList.length - screenedTickers.length
    }
}





export const extractEtfsForSankey = (etfs, maxPerEtf = 30) => {
  const nodes = [];
  const links = [];
  const nodeSet = new Set();  // To keep track of unique nodes (ETFs, sectors, and stocks)

  // Step through each ETF
  etfs.forEach((etf) => {
    const { symbol: etfSymbol, weight: etfWeight, holdings } = etf;

    // Limit the number of stocks per ETF using maxPerEtf
    const reducedHoldings = holdings.slice(0, maxPerEtf);

    // Add ETF as a node if not already added
    if (!nodeSet.has(etfSymbol)) {
      nodes.push({ id: etfSymbol });
      nodeSet.add(etfSymbol);
    }

    // Group holdings by sector
    const sectorsMap = {};

    reducedHoldings.forEach((holding) => {
      const { symbol: stockSymbol, wgt: stockWeight, sector } = holding;

      // Add stock to its sector group
      if (!sectorsMap[sector]) {
        sectorsMap[sector] = [];
      }
      sectorsMap[sector].push({ stockSymbol, stockWeight });
    });

    // Step through each sector
    Object.keys(sectorsMap).forEach((sector) => {

      // Add sector as a node if not already added
      if (!nodeSet.has(sector)) {
        nodes.push({ id: sector });
        nodeSet.add(sector);
      }

      // Calculate total weight flowing from ETF to the sector
      const sectorStocks = sectorsMap[sector];
      const sectorFlowWeight = sectorStocks.reduce((sum, stock) => sum + stock.stockWeight, 0) * etfWeight;

      // Add link from ETF to sector
      links.push({
        source: sector,
        target: etfSymbol,
        value: sectorFlowWeight
      });

      // Step through each stock in the sector
      sectorStocks.forEach((stock) => {
        const { stockSymbol, stockWeight } = stock;

        // Add stock as a node if not already added
        if (!nodeSet.has(stockSymbol)) {
          nodes.push({ id: stockSymbol });
          nodeSet.add(stockSymbol);
        }

        // Calculate flow weight from sector to stock
        const flowWeight = etfWeight * stockWeight;

        // Add link from sector to stock
        links.push({
          source: etfSymbol,
          target: stockSymbol,
          value: flowWeight
        });
      });
    });
  });

  // Return formatted data for Nivo Sankey
  return { nodes, links };
};


/**
 * Calculate the percentage contributions of each ETF to the final filled holdings.
 *
 * @param {Array} filledHoldings - The array of filled holdings, each containing a 'parents' array and 'wgt'.
 * @param {Object} etfData - The ETF data, containing ETF weights, names, and other attributes.
 *
 * @returns {Object} - An object mapping ETF symbols to their percentage representation in the final portfolio.
 */
/**
 * Calculate the percentage contributions of each ETF to the final filled holdings.
 *
 * @param {Array} filledHoldings - The array of filled holdings, each containing a 'parents' array and 'wgt'.
 * @param {Object} etfData - The ETF data, containing ETF weights, holdings, and other attributes.
 *
 * @returns {Object} - An object mapping ETF symbols to their percentage representation in the final portfolio.
 */
export const calculateEtfContributions = (filledHoldings, etfData) => {
    // Initialize an object to track total weights contributed by each ETF
    const etfContributions = {};

    // Calculate the total weight of all filled holdings
    const totalPortfolioWeight = filledHoldings.reduce((sum, holding) => sum + holding.wgt, 0);

    // Iterate over each holding to distribute its weight based on the ETF's original weight
    filledHoldings.forEach(holding => {
        // Retrieve the holding's weight in the final portfolio
        const holdingWeight = holding.wgt;

        // Ensure that each holding has parents (ETFs) to contribute to
        if (holding.parents && holding.parents.length > 0) {
            // Iterate over each parent ETF
            holding.parents.forEach(parentEtf => {
                const parentEtfData = etfData[parentEtf];

                if (parentEtfData) {
                    // Find the stock's original weight within this parent ETF
                    const etfHolding = parentEtfData.holdings.find(h => h.symbol === holding.symbol);
                    const originalWeightInEtf = etfHolding ? etfHolding.wgt : 0;

                    // Calculate the stock's contribution to the ETF based on its weight within that ETF
                    const weightedContribution = originalWeightInEtf;

                    // Add this contribution to the ETF's total in etfContributions
                    if (!etfContributions[parentEtf]) {
                        etfContributions[parentEtf] = 0;
                    }
                    etfContributions[parentEtf] += weightedContribution;
                }
            });
        }
    });

    // Ensure that every ETF in etfData is represented in the output, assigning 0 if not represented
    Object.keys(etfData).forEach(etfSymbol => {
        if (!etfContributions[etfSymbol]) {
            etfContributions[etfSymbol] = 0;
        }
    });

    // Convert raw contribution weights to percentages relative to the total portfolio weight
    Object.keys(etfContributions).forEach(etf => {
        etfContributions[etf] = (etfContributions[etf] / totalPortfolioWeight);
    });

    return etfContributions;
};




export const countTickersBySector = (tickers)=>{
    const sectorCounts = {};
    tickers.forEach(({sector}) => {
        if (sectorCounts[sector]) {
            sectorCounts[sector] += 1;
        } else {
            sectorCounts[sector] = 1;
        }
    });
    return sectorCounts;
}